import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CLOSURE_CONDITIONS,
  CLOSURE_CONDITION_HELP,
  CLOSURE_CONDITION_LABELS,
  CLOSURE_LONG_TERM_DAYS,
  CLOSURE_NOTIFY_DELAY_MIN,
  CLOSURE_REOPEN_CHOICES,
  CLOSURE_COLORS,
  CLOSURE_REASONS,
  CLOSURE_REASON_LABELS,
  cleanClosureInput,
  closureDraftError,
  compareClosures,
  isClosureLive,
} from '@ww/shared';
import type { ClosureCondition, ClosureReason, NotificationGroup, RoadClosure } from '@ww/shared';
import { api } from '../api';
import WeatherMap from '../components/WeatherMap';
import { ErrorBox, Loading, PageHeader } from '../components/ui';
import { useLive } from '../hooks/useLive';
import { I, Icon } from '../lib/icons';
import { canEditClosures, errorMessage, isAdmin } from '../lib/status';
import { fmtDateTime } from '../lib/time';

/**
 * Road closures the county enters itself.
 *
 * Structurally a clone of Assets.tsx — the same draft/save/notice lifecycle and the same
 * map-based location picking — because that is the CRUD-with-a-map pattern this codebase
 * already has and following it is worth more than improving on it.
 *
 * The one addition is that a closure can be a LINE as well as a point: a road is shut
 * *between* two places, and a single pin cannot say that.
 */

type Draft = {
  roadName: string;
  notes: string;
  reason: ClosureReason;
  condition: ClosureCondition;
  /**
   * What the dropdown is showing:
   *   ''      keep whatever is already stored (only reachable when editing)
   *   'none'  not known
   *   '30'    minutes from now
   */
  reopen: string;
  /** The stored time, carried so 'keep' survives even if the row ages out mid-edit. */
  keepIso: string | null;
  longTerm: boolean;
  /** Groups to text. Empty means nobody, and is honoured literally. */
  notifyGroupIds: string[];
};

const EMPTY: Draft = {
  roadName: '',
  notes: '',
  reason: 'flood',
  condition: 'closed',
  reopen: 'none',
  keepIso: null,
  longTerm: false,
  notifyGroupIds: [],
};

/** Turn a chosen duration into the absolute time the API stores. */
function reopenToIso(choice: string, now = Date.now()): string | null {
  if (choice === 'none' || choice === '') return null;
  const mins = Number(choice);
  return Number.isFinite(mins) ? new Date(now + mins * 60_000).toISOString() : null;
}

/** "9h ago" — how old a closure is, which is the question that decides whether to trust it. */
function ageLabel(iso: string, now: number): string {
  const mins = Math.max(0, Math.round((now - Date.parse(iso)) / 60_000));
  if (mins < 60) return `${mins}m ago`;
  const h = Math.floor(mins / 60);
  return h < 48 ? `${h}h ago` : `${Math.floor(h / 24)}d ago`;
}

export default function Closures() {
  const { me, tenant, closures: liveClosures, refetchClosures } = useLive();
  const canEdit = canEditClosures(me?.user.role);
  const admin = isAdmin(me?.user.role);
  const tz = tenant?.timezone;

  const [params, setParams] = useSearchParams();
  const selectedId = params.get('sel');

  const [showEnded, setShowEnded] = useState(false);
  const [ended, setEnded] = useState<RoadClosure[] | null>(null);
  const [form, setForm] = useState<Draft | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [verts, setVerts] = useState<[number, number][]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  /*
   * The groups live on the alert policy, which this page has no other reason to load.
   * Fetched once rather than threaded through useLive: it is one small GET, and the
   * form is unusable without the names.
   */
  const [groups, setGroups] = useState<NotificationGroup[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .alertPolicy()
      .then((p) => {
        if (!cancelled) setGroups(p.notificationGroups ?? []);
      })
      .catch(() => {
        if (!cancelled) setGroups([]);
      });
    return () => {
      cancelled = true;
    };
  }, [tenant?._id]);

  // Ages are on screen; a clock that never moves makes "9h ago" quietly wrong.
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(t);
  }, []);

  useEffect(() => {
    if (!notice) return;
    const t = window.setTimeout(() => setNotice(null), 4000);
    return () => window.clearTimeout(t);
  }, [notice]);

  const loadEnded = useCallback(async () => {
    try {
      const all = await api.closures(true);
      setEnded(all.filter((c) => !isClosureLive(c)));
    } catch (e) {
      setError(errorMessage(e));
    }
  }, []);

  useEffect(() => {
    if (showEnded) void loadEnded();
  }, [showEnded, loadEnded]);

  const live = useMemo(() => [...(liveClosures ?? [])].sort(compareClosures), [liveClosures]);
  const selected = live.find((c) => c._id === selectedId) ?? null;

  const openNew = () => {
    setEditingId(null);
    setForm({ ...EMPTY });
    setVerts([]);
    setError(null);
  };

  const openEdit = (c: RoadClosure) => {
    setEditingId(c._id);
    setForm({
      roadName: c.roadName,
      notes: c.notes,
      reason: c.reason,
      condition: c.condition,
      /*
       * Always '' — keep what is stored — never a guessed duration. A closure saved as
       * "1 hour" at 10:00 is stored as 11:00; opening the form at 10:30 and showing
       * "1 hour" would silently push it to 11:30 on save. The stored time is shown as
       * text beside the dropdown instead, and the dropdown only ever CHANGES it.
       */
      reopen: c.expectedReopenAt ? '' : 'none',
      keepIso: c.expectedReopenAt,
      longTerm: c.longTerm === true,
      notifyGroupIds: c.notifyGroupIds ?? [],
    });
    setVerts(c.geometry.type === 'Point' ? [c.geometry.coordinates] : c.geometry.coordinates);
    setError(null);
  };

  const closeForm = () => {
    setForm(null);
    setEditingId(null);
    setVerts([]);
  };

  const geometry = useMemo(() => {
    if (verts.length === 1) return { type: 'Point' as const, coordinates: verts[0]! };
    if (verts.length >= 2) return { type: 'LineString' as const, coordinates: verts };
    return null;
  }, [verts]);

  const save = async () => {
    if (!form) return;
    // The same validator the server runs, so the form cannot post something the API
    // will reject for a reason it never explained.
    const draft = cleanClosureInput({
      ...form,
      geometry,
      expectedReopenAt: form.reopen === '' ? form.keepIso : reopenToIso(form.reopen),
      longTerm: form.longTerm,
      notifyGroupIds: form.notifyGroupIds,
    });
    const problem = closureDraftError(draft);
    if (problem || !draft.geometry) {
      setError(problem ?? 'Mark the closure on the map.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const body = {
        roadName: draft.roadName,
        notes: draft.notes,
        reason: draft.reason,
        condition: draft.condition,
        geometry: draft.geometry,
        expectedReopenAt: draft.expectedReopenAt,
        longTerm: draft.longTerm,
        notifyGroupIds: draft.notifyGroupIds,
      };
      if (editingId) await api.updateClosure(editingId, body);
      else await api.createClosure(body);
      await refetchClosures();
      setNotice(editingId ? 'Closure updated.' : 'Closure posted.');
      closeForm();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const reopen = async (c: RoadClosure) => {
    setBusy(true);
    setError(null);
    try {
      await api.reopenClosure(c._id);
      await refetchClosures();
      setNotice(`${c.roadName} marked open.`);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (c: RoadClosure) => {
    setBusy(true);
    setError(null);
    try {
      await api.deleteClosure(c._id);
      await refetchClosures();
      setNotice(`Removed the entry for ${c.roadName}.`);
      if (selectedId === c._id) setParams({});
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  /** How many phones the current selection actually reaches — the number that matters. */
  const recipientCount = (groups ?? [])
    .filter((g) => form?.notifyGroupIds.includes(g.id))
    .reduce((n, g) => n + (g.recipients ?? []).length, 0);

  if (!liveClosures) return <Loading label="Loading closures…" />;

  const rows = showEnded ? (ended ?? []) : live;

  return (
    <div className="page">
      <PageHeader
        title="Road closures"
        sub={`Entered by ${tenant?.name ?? 'this county'}. ${live.length} open now.`}
      >
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            className="chip hov"
            aria-pressed={showEnded}
            onClick={() => setShowEnded((v) => !v)}
          >
            {showEnded ? 'Showing past' : 'Show past'}
          </button>
          {canEdit && !form && (
            <button type="button" className="btn btn-primary hov" onClick={openNew}>
              <Icon d={I.plus} size={18} /> Add closure
            </button>
          )}
        </div>
      </PageHeader>

      {notice && <div className="ok-box">{notice}</div>}
      <ErrorBox error={error} />
      {!canEdit && (
        <div className="warn-box">
          You can see closures but not post them. A dispatcher or county administrator account can.
        </div>
      )}

      <div className="assets-grid">
        <section className="panel" style={{ display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
          {form ? (
            /*
             * The form scrolls, like the list beside it does. The panel is
             * `overflow: hidden` so the map keeps its height, and without this the form
             * simply got clipped at the bottom — which since "Text these groups" was
             * added means Save is off-screen and unreachable. A form you cannot submit
             * is worse than one that looks slightly cramped.
             */
            <div className="sound-body scroll" style={{ flexGrow: 1, minHeight: 0 }}>
              <div className="lbl">{editingId ? 'Edit closure' : 'New closure'}</div>

              <label className="field">
                <span className="lbl">Road</span>
                <input
                  className="fld"
                  autoFocus
                  placeholder="Hwy 64 at Cypress Creek"
                  value={form.roadName}
                  onChange={(e) => setForm({ ...form, roadName: e.target.value })}
                />
              </label>

              <div className="field">
                <span className="lbl">Why</span>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {CLOSURE_REASONS.map((r) => (
                    <button
                      key={r}
                      type="button"
                      className="chip hov"
                      aria-pressed={form.reason === r}
                      onClick={() => setForm({ ...form, reason: r })}
                    >
                      {CLOSURE_REASON_LABELS[r]}
                    </button>
                  ))}
                </div>
              </div>

              <div className="field">
                <span className="lbl">How bad</span>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {CLOSURE_CONDITIONS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      className="chip hov"
                      aria-pressed={form.condition === c}
                      onClick={() => setForm({ ...form, condition: c })}
                    >
                      {CLOSURE_CONDITION_LABELS[c]}
                    </button>
                  ))}
                </div>
                <span className="muted" style={{ fontSize: 12.5 }}>
                  {CLOSURE_CONDITION_HELP[form.condition]}
                </span>
              </div>

              <div className="field">
                <span className="lbl">Where</span>
                <div className="muted" style={{ fontSize: 13 }}>
                  {verts.length === 0
                    ? 'Tap the map: once for a spot, or several times to trace the closed stretch.'
                    : verts.length === 1
                      ? 'One point set. Tap again to make it a stretch of road.'
                      : `${verts.length} points — the closed stretch.`}
                </div>
                {verts.length > 0 && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                    <button type="button" className="btn btn-sm btn-ghost hov" onClick={() => setVerts(verts.slice(0, -1))}>
                      Undo point
                    </button>
                    <button type="button" className="btn btn-sm btn-ghost hov" onClick={() => setVerts([])}>
                      Clear
                    </button>
                  </div>
                )}
              </div>

              <label className="field">
                <span className="lbl">Expected to reopen</span>
                <select
                  className="fld"
                  value={form.reopen}
                  onChange={(e) => setForm({ ...form, reopen: e.target.value })}
                >
                  {/* Only offered while editing something that already has a time. */}
                  {form.keepIso && <option value="">Leave as {fmtDateTime(form.keepIso, tz)}</option>}
                  <option value="none">Not known</option>
                  {CLOSURE_REOPEN_CHOICES.map((c) => (
                    <option key={c.minutes} value={String(c.minutes)}>
                      In about {c.label}
                    </option>
                  ))}
                </select>
                <span className="muted" style={{ fontSize: 12.5 }}>
                  {form.longTerm
                    ? `Long-term: this stays on the map for up to ${CLOSURE_LONG_TERM_DAYS} days.`
                    : form.reopen === 'none'
                      ? 'Not known: this closure drops off the map after 24 hours unless someone updates it.'
                      : 'A closure stays on the map a while past this, then drops off on its own.'}
                </span>
              </label>

              <label className="check">
                <input
                  type="checkbox"
                  checked={form.longTerm}
                  onChange={(e) => setForm({ ...form, longTerm: e.target.checked })}
                />
                <span>
                  Closed longer than a day
                  <div className="muted" style={{ fontSize: 12.5 }}>
                    A bridge out, or a long rebuild. Keeps it on the map for up to{' '}
                    {CLOSURE_LONG_TERM_DAYS} days instead of dropping off overnight. Saving it again
                    resets that.
                  </div>
                </span>
              </label>

              <div className="field">
                <span className="lbl">Text these groups</span>
                {groups === null && <span className="muted" style={{ fontSize: 13 }}>Loading groups…</span>}
                {groups !== null && groups.length === 0 && (
                  <span className="muted" style={{ fontSize: 13 }}>
                    No notification groups are set up for this county, so nobody can be texted about
                    this closure.
                  </span>
                )}
                {groups !== null && groups.length > 0 && (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {groups.map((g) => {
                      const on = form.notifyGroupIds.includes(g.id);
                      return (
                        <button
                          key={g.id}
                          type="button"
                          className="chip hov"
                          aria-pressed={on}
                          onClick={() =>
                            setForm({
                              ...form,
                              notifyGroupIds: on
                                ? form.notifyGroupIds.filter((x) => x !== g.id)
                                : [...form.notifyGroupIds, g.id],
                            })
                          }
                        >
                          {g.name} · {(g.recipients ?? []).length}
                        </button>
                      );
                    })}
                  </div>
                )}
                {/*
                  * This is the control that puts a text message on real phones, so it says
                  * exactly what Save does — including the five-minute window, which is the
                  * only thing standing between a mistyped road name and the whole county
                  * reading it.
                  */}
                {form.notifyGroupIds.length === 0 ? (
                  <span className="muted" style={{ fontSize: 12.5, lineHeight: 1.5 }}>
                    <strong>Nobody will be texted.</strong> This closure will appear on the map and
                    in the app, and no message will be sent to anyone.
                  </span>
                ) : (
                  <span className="muted" style={{ fontSize: 12.5, lineHeight: 1.5 }}>
                    Saving will text {recipientCount} phone{recipientCount === 1 ? '' : 's'} in{' '}
                    {form.notifyGroupIds.length} group{form.notifyGroupIds.length === 1 ? '' : 's'}.
                    Nothing is sent for about {CLOSURE_NOTIFY_DELAY_MIN} minutes: if you edit this
                    closure in that time the corrected version is what goes out, and if you delete it
                    no message is sent at all. The same groups get a second text when you mark the
                    road open.
                  </span>
                )}
              </div>

              <label className="field">
                <span className="lbl">Notes</span>
                <textarea
                  className="fld"
                  placeholder="Water over roadway, about 8 inches. Barricades set."
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                />
              </label>

              <div className="closure-form-actions">
                <button type="button" className="btn btn-primary hov" onClick={() => void save()} disabled={busy}>
                  {busy ? 'Saving…' : editingId ? 'Save changes' : 'Post closure'}
                </button>
                <button type="button" className="btn btn-ghost hov" onClick={closeForm} disabled={busy}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="scroll" style={{ flexGrow: 1, minHeight: 0 }}>
              {rows.length === 0 && (
                <div className="empty">
                  {showEnded ? 'Nothing in the history yet.' : 'No roads reported closed right now.'}
                </div>
              )}
              {rows.map((c) => (
                <div
                  key={c._id}
                  className="closure-row"
                  aria-current={c._id === selectedId ? 'true' : undefined}
                >
                  <span className="closure-swatch" style={{ background: CLOSURE_COLORS[c.condition] }} aria-hidden />
                  <button
                    type="button"
                    className="closure-main hov"
                    onClick={() => setParams(c._id === selectedId ? {} : { sel: c._id })}
                  >
                    <span className="closure-name">{c.roadName}</span>
                    <span className="muted closure-meta">
                      {CLOSURE_REASON_LABELS[c.reason]} · {CLOSURE_CONDITION_LABELS[c.condition]} ·{' '}
                      {ageLabel(c.createdAt, now)}
                    </span>
                    {c.nearAssets.length > 0 && (
                      <span className="closure-near">
                        <Icon d={I.pin} size={13} />
                        {c.nearAssets
                          .slice(0, 2)
                          .map((a) => `${a.name} (${a.distanceMiles.toFixed(1)} mi)`)
                          .join(', ')}
                        {c.nearAssets.length > 2 ? ` +${c.nearAssets.length - 2}` : ''}
                      </span>
                    )}
                    {c.notes && <span className="muted closure-notes">{c.notes}</span>}
                  </button>
                  {canEdit && !showEnded && (
                    <div className="closure-actions">
                      <button type="button" className="btn btn-sm btn-secondary hov" onClick={() => openEdit(c)}>
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-secondary hov"
                        disabled={busy}
                        onClick={() => void reopen(c)}
                      >
                        Open
                      </button>
                      {admin && (
                        <button
                          type="button"
                          className="btn btn-sm btn-danger hov"
                          disabled={busy}
                          onClick={() => void remove(c)}
                        >
                          <Icon d={I.x} size={14} />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="panel" style={{ position: 'relative', minHeight: 360, overflow: 'hidden' }}>
          <WeatherMap
            tenant={tenant}
            alerts={[]}
            assets={[]}
            closures={form ? [] : live}
            onClosureClick={(id) => setParams({ sel: id })}
            drawing={!!form}
            drawShape="line"
            drawVertices={verts}
            placingHint={
              form
                ? 'Tap the road. One tap marks a spot; keep tapping to trace the closed stretch.'
                : undefined
            }
            onDrawVertex={(lat, lon) => setVerts((v) => [...v, [lon, lat]])}
            fitToGeometry={undefined}
            showRadar={false}
            compact
          />
        </section>
      </div>

      {selected && !form && (
        <div className="muted" style={{ fontSize: 12.5 }}>
          {selected.roadName} · reported by {selected.reportedBy.name} at {fmtDateTime(selected.createdAt, tz)}
          {selected.expectedReopenAt ? ` · expected open ${fmtDateTime(selected.expectedReopenAt, tz)}` : ''}
        </div>
      )}
    </div>
  );
}
